import { useEffect, useState, useMemo } from 'react';
import axios from 'axios';
import { ENDPOINTS } from '../../../Utils/Endpoints';
import { Models } from '../../../../General/Utils/ModelsGeneral';
import './../../../../../style/ContainersStyles.css';
import '../../../Styles/index.css';
import AlertDialog from '../../../../Lms/Components/Shared/AlertDialog';
import { isSessionSuperAdmin, getSessionEmpresaId } from '../../../../../utils/SessionUser';

export default function GestorDocumentalModal({
    mode = 'new',
    idEmpleado: propIdEmpleado,
    nombreEmpleadoPre,
    onClose,
    inline = false,
}) {
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);

    const [alertDialog, setAlertDialog] = useState({
        open: false,
        title: 'Información',
        message: '',
        variant: 'info'
    });

    // Data Sources
    const [allAreas, setAllAreas] = useState([]);
    const [allEmpleados, setAllEmpleados] = useState([]); // Solo para modo 'new'

    // Selection State
    const [selectedEmpleadoId, setSelectedEmpleadoId] = useState(propIdEmpleado || 0);

    // Assignments State
    const [initialAssignments, setInitialAssignments] = useState([]);
    const [currentAssignments, setCurrentAssignments] = useState([]);

    // Drag & Drop UI State
    const [dragOverColumn, setDragOverColumn] = useState(null);

    // --- Loading Data ---
    useEffect(() => {
        const loadCatalogs = async () => {
            setLoading(true);
            try {
                // Fetch basic catalogs
                const [resAreas, resEmpleados, resPersonas] = await Promise.all([
                    axios.get(ENDPOINTS.areasNegocios),
                    axios.get(ENDPOINTS.empleados),
                    axios.get(ENDPOINTS.personas)
                ]);

                const rawAreas = (resAreas.data || []).map(a => Models.areasNegocios(a));
                
                const isSuperAdmin = isSessionSuperAdmin();
                const sessionEmpresaId = getSessionEmpresaId();
                
                // 1. Aislamiento Multi-Tenant
                const filteredAreas = rawAreas.filter(r => {
                    if (isSuperAdmin) return true;
                    // Solo ver lo de mi empresa o lo que sea Global (idEmpresa null/0)
                    return r.idEmpresa === sessionEmpresaId || !r.idEmpresa || r.idEmpresa === 0;
                });

                setAllAreas(filteredAreas);

                // Process employees for dropdown (only needed if mode is new or to resolve names)
                const personas = (resPersonas.data || []).map(p => Models.personas(p));
                const emps = (resEmpleados.data || []).map(e => Models.empleado(e));

                const empsWithNames = emps.map(e => {
                    const p = personas.find(per => per.idPersona === e.idPersona);
                    const name = p
                        ? `${p.nombre || ''} ${p.apellidoPaterno || ''} ${p.apellidoMaterno || ''}`.trim()
                        : `Empleado #${e.idEmpleado}`;
                    return { ...e, nombreCompleto: name };
                });

                empsWithNames.sort((a, b) => a.nombreCompleto.localeCompare(b.nombreCompleto));
                setAllEmpleados(empsWithNames);

            } catch (e) {
                console.error(e);
                setAlertDialog({
                    open: true,
                    title: 'Error',
                    message: "Error cargando catálogos.",
                    variant: 'error'
                });
            } finally {
                setLoading(false);
            }
        };

        loadCatalogs();
    }, []);

    // Load Assignments when user is selected
    useEffect(() => {
        if (!selectedEmpleadoId) {
            setCurrentAssignments([]);
            setInitialAssignments([]);
            return;
        }

        const loadAssignments = async () => {
            setLoading(true);
            try {
                // Fetch all assignments and filter by employee (API limitation workaround)
                const res = await axios.get(ENDPOINTS.gestorDocumental);
                const allRaw = res.data || [];

                // Manual normalization for GestorDocumental as there is no model yet
                const all = allRaw.map(g => ({
                    idGestorDocumental: g.idGestorDocumental ?? g.IdGestorDocumental ?? 0,
                    idEmpleado: g.idEmpleado ?? g.IdEmpleado ?? 0,
                    idAreaNegocio: g.idAreaNegocio ?? g.IdAreaNegocio ?? 0,
                    activo: g.activo ?? g.Activo ?? false
                }));

                const userAssignments = all.filter(x => x.idEmpleado === selectedEmpleadoId && x.activo);

                setInitialAssignments(userAssignments);
                setCurrentAssignments(userAssignments);
            } catch (e) {
                console.error(e);
                setAlertDialog({
                    open: true,
                    title: 'Error',
                    message: "Error cargando asignaciones del empleado.",
                    variant: 'error'
                });
            } finally {
                setLoading(false);
            }
        };

        loadAssignments();
    }, [selectedEmpleadoId]);


    // --- Computed ---

    // Areas NOT assigned
    const availableAreas = useMemo(() => {
        const assignedIds = new Set(currentAssignments.map(a => a.idAreaNegocio));
        return allAreas.filter(area => !assignedIds.has(area.idAreaNegocio));
    }, [allAreas, currentAssignments]);

    // Enriched Assigned (Attach Name)
    const enrichedAssigned = useMemo(() => {
        return currentAssignments.map(item => {
            const area = allAreas.find(a => a.idAreaNegocio === item.idAreaNegocio);
            return {
                ...item,
                nombreArea: area ? area.nombre : `Area #${item.idAreaNegocio}`
            };
        });
    }, [currentAssignments, allAreas]);

    // Check changes
    const hasChanges = useMemo(() => {
        if (initialAssignments.length !== currentAssignments.length) return true;
        const initialAreaIds = new Set(initialAssignments.map(x => x.idAreaNegocio));
        for (let item of currentAssignments) {
            if (!initialAreaIds.has(item.idAreaNegocio)) return true;
        }
        return false;
    }, [initialAssignments, currentAssignments]);

    // --- Handlers ---

    const handleDragStart = (e, item, source) => {
        e.dataTransfer.setData('application/json', JSON.stringify({ item, source }));
        e.dataTransfer.effectAllowed = 'move';
    };

    const handleDragOver = (e, targetColumn) => {
        e.preventDefault();
        if (dragOverColumn !== targetColumn) {
            setDragOverColumn(targetColumn);
        }
    };

    const handleDrop = (e, targetColumn) => {
        e.preventDefault();
        setDragOverColumn(null);

        const dataStr = e.dataTransfer.getData('application/json');
        if (!dataStr) return;

        const { item, source } = JSON.parse(dataStr);
        if (source === targetColumn) return;

        if (targetColumn === 'assigned' && source === 'available') {
            // Add Area (item is Area object)
            addAreaLocal(item);
        } else if (targetColumn === 'available' && source === 'assigned') {
            // Remove Assignment (item is Assignment object)
            removeAreaLocal(item);
        }
    };

    const addAreaLocal = (area) => {
        const exists = currentAssignments.some(x => x.idAreaNegocio === area.idAreaNegocio);
        if (exists) return;

        const newAssignment = {
            idGestorDocumental: 0, // 0 indicates New
            idEmpleado: selectedEmpleadoId,
            idAreaNegocio: area.idAreaNegocio,
            activo: true
        };
        setCurrentAssignments(prev => [...prev, newAssignment]);
    };

    const removeAreaLocal = (assignment) => {
        setCurrentAssignments(prev => prev.filter(x => x.idAreaNegocio !== assignment.idAreaNegocio));
    };


    const handleSave = async () => {
        if (!selectedEmpleadoId) {
            setAlertDialog({
                open: true,
                title: 'Aviso',
                message: "Debes seleccionar un empleado.",
                variant: 'info'
            });
            return;
        }

        setSaving(true);
        try {
            // 1. To Add (In current but not initial)
            const initialIds = new Set(initialAssignments.map(x => x.idAreaNegocio));
            const toAdd = currentAssignments.filter(x => !initialIds.has(x.idAreaNegocio));

            // 2. To Remove (In initial but not current)
            const currentIds = new Set(currentAssignments.map(x => x.idAreaNegocio));
            const toRemove = initialAssignments.filter(x => !currentIds.has(x.idAreaNegocio));

            // 3. Execute
            const promises = [];

            // Inserts
            for (const item of toAdd) {
                const dto = {
                    idGestorDocumental: 0,
                    idEmpleado: selectedEmpleadoId,
                    idAreaNegocio: item.idAreaNegocio,
                    activo: true
                };
                promises.push(axios.post(ENDPOINTS.gestorDocumental, dto));
            }

            // Deletes
            for (const item of toRemove) {
                if (item.idGestorDocumental > 0) {
                    promises.push(axios.delete(ENDPOINTS.gestorDocumentalById(item.idGestorDocumental)));
                }
            }

            await Promise.all(promises);

            setAlertDialog({
                open: true,
                title: 'Éxito',
                message: "Cambios guardados correctamente.",
                variant: 'success',
                onConfirm: () => onClose(true)
            });

        } catch (e) {
            console.error(e);
            setAlertDialog({
                open: true,
                title: 'Error',
                message: "Error al guardar cambios.",
                variant: 'error'
            });
        } finally {
            setSaving(false);
        }
    };

    // --- Render ---

    const Header = (
        <div className="roles-topbar" style={{ marginBottom: '0.6rem', display: 'flex', justifyContent: 'space-between', alignItems: 'center', paddingRight: 16 }}>
            <h3 className="roles-title" style={{ margin: 0 }}>
                {mode === 'new' ? 'Nueva Asignación de Áreas' : `Editando: ${nombreEmpleadoPre || 'Empleado'}`}
            </h3>
            <button className="roles-btn" onClick={() => onClose(false)} aria-label="Cerrar">✕</button>
        </div>
    );

    const EmployeeSelector = mode === 'new' && (
        <div style={{ padding: '0 16px', marginBottom: '1rem' }}>
            <label className="roles-text" style={{ display: 'block', marginBottom: 6 }}>Selecciona un Empleado:</label>
            <select
                className="infoField"
                value={selectedEmpleadoId}
                onChange={e => setSelectedEmpleadoId(Number(e.target.value))}
            >
                <option value={0}>-- Selecciona --</option>
                {allEmpleados.map(e => (
                    <option key={e.idEmpleado} value={e.idEmpleado}>{e.nombreCompleto}</option>
                ))}
            </select>
        </div>
    );

    const DragAndDropArea = (
        <div className="dnd-container" style={{ margin: '0 16px' }}>
            {/* Left: Available */}
            <div
                className={`dnd-column ${dragOverColumn === 'available' ? 'drag-over' : ''}`}
                onDragOver={(e) => handleDragOver(e, 'available')}
                onDrop={(e) => handleDrop(e, 'available')}
            >
                <div className="dnd-header">Áreas Disponibles</div>
                {availableAreas.length === 0 ? (
                    <div className="dnd-empty">No hay áreas disponibles</div>
                ) : (
                    availableAreas.map(area => (
                        <div
                            key={area.idAreaNegocio}
                            className="dnd-item"
                            draggable={!saving && !!selectedEmpleadoId}
                            onDragStart={(e) => handleDragStart(e, area, 'available')}
                        >
                            <span>{area.nombre}</span>
                            <button
                                type="button"
                                onClick={() => addAreaLocal(area)}
                                style={{
                                    background: 'none', border: 'none', fontSize: '1.2em',
                                    color: '#28a745', cursor: 'pointer', padding: '0 5px'
                                }}
                            >
                                ＋
                            </button>
                        </div>
                    ))
                )}
            </div>

            {/* Right: Assigned */}
            <div
                className={`dnd-column ${dragOverColumn === 'assigned' ? 'drag-over' : ''}`}
                onDragOver={(e) => handleDragOver(e, 'assigned')}
                onDrop={(e) => handleDrop(e, 'assigned')}
            >
                <div className="dnd-header">Áreas Asignadas</div>
                {enrichedAssigned.length === 0 ? (
                    <div className="dnd-empty">
                        {!selectedEmpleadoId ? 'Selecciona un empleado primero' : 'Arrastra áreas aquí'}
                    </div>
                ) : (
                    enrichedAssigned.map(item => (
                        <div
                            key={item.idAreaNegocio} // Use area ID as key
                            className="dnd-item"
                            draggable={!saving}
                            onDragStart={(e) => handleDragStart(e, item, 'assigned')}
                        >
                            <span>{item.nombreArea}</span>
                            <button
                                type="button"
                                onClick={() => removeAreaLocal(item)}
                                style={{
                                    background: 'none', border: 'none', fontSize: '1.2em',
                                    color: '#dc3545', cursor: 'pointer', padding: '0 5px'
                                }}
                            >
                                ×
                            </button>
                        </div>
                    ))
                )}
            </div>
        </div>
    );

    const Footer = (
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '.6rem', marginTop: '1.5rem', paddingRight: 16 }}>
            <button className="roles-btn" onClick={() => onClose(false)}>Cancelar</button>
            <button
                className="btn-action"
                onClick={handleSave}
                disabled={!hasChanges || saving || !selectedEmpleadoId}
                style={{
                    opacity: (!hasChanges || saving || !selectedEmpleadoId) ? 0.5 : 1,
                    cursor: (!hasChanges || saving || !selectedEmpleadoId) ? 'not-allowed' : 'pointer'
                }}
            >
                {saving ? 'Guardando...' : 'Guardar Cambios'}
            </button>
        </div>
    );

    if (inline) {
        return (
            <div className="roles-inline-card" style={{ display: 'flex', flexDirection: 'column', gap: 0, paddingBottom: 16 }}>
                {Header}
                {EmployeeSelector}
                {loading ? <div style={{ padding: 20 }}>Cargando...</div> : DragAndDropArea}
                {Footer}

                <AlertDialog
                    open={alertDialog.open}
                    title={alertDialog.title}
                    message={alertDialog.message}
                    variant={alertDialog.variant}
                    onClose={() => {
                        setAlertDialog({ ...alertDialog, open: false });
                        if (alertDialog.onConfirm) alertDialog.onConfirm();
                    }}
                    buttonText="Aceptar"
                />
            </div>
        );
    }
    return null;
}
